import * as React from 'react';
import styled, { css, keyframes, withTheme } from 'styled-components';
import { rem } from 'polished';
import { FlexRow, FlexRowCenterVertical } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { ZemplinLoaderWrapper } from '../../..';
import { TableWrapper } from '../General/TableWrapper';
import { TableRow, SecondTableTh } from '../General/Table';
import { OrderRow } from './OrderRow';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { getOrderRowInfo } from '../../../utilities/selectors';
import { Link } from 'react-router';
import { getOrderColor } from './OrderRow';

interface Props {
  orders: any;
  isFetching: boolean;
  theme: any;
  download: (publicId) => void;
}

function Orders({ orders, isFetching, theme, download }: Props) {
  const columns = [
    { name: 'Obj. číslo', align: 'left' },
    { name: 'Dátum a čas', align: 'left' },
    { name: 'Suma', align: 'right', minWidth: rem(250) },
    { name: 'Faktúra', align: 'left' },
    { name: 'Stav', align: 'left' },
  ];
  const headers =
    columns &&
    columns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
      </SecondTableTh>
    ));

  const orderRows =
    orders &&
    orders.map((order: any) => (
      <OrderRow key={order.order_id} order={order} download={download} />
    ));

  const renderMobileDownloadIcon = () => {
    return (
      <FileWrapper>
        <SvgIcon
          icon={IconType.download}
          alt="Stiahnuť faktúru"
          title="Stiahnuť faktúru"
          marginRight={8}
          width={24}
          height={24}
          viewBox="0 0 18 18"
        />
        <Desciption>{__('Stiahnuť')}</Desciption>
      </FileWrapper>
    );
  };

  const renderMobileStatus = (status, color) => {
    return (
      <Value color={color}>
        {status === 'Zrušená' && (
          <>
            <SvgIcon
              icon={IconType.xSmall}
              alt={__('Zrušená')}
              width={12}
              height={12}
              viewBox="2 2 14 14"
              marginRight={8}
              fill={'#EB676C'}
            />
          </>
        )}
        {status === 'Prijatá' && (
          <>
            <SvgIcon
              icon={IconType.checked}
              alt={__('Prijatá')}
              width={12}
              height={12}
              viewBox="2 2 12 12"
              marginRight={8}
              fill={'#A5BBA2'}
            />
          </>
        )}
        {status}
      </Value>
    );
  };

  return (
    <Wrapper>
      {isFetching ? (
        <ZemplinLoaderWrapper height={500} />
      ) : (
        <>
          {/* <HorizontalScrollButton>
            <SvgIcon
              icon={IconType.scroll}
              alt={__('Scroll')}
              width={23}
              height={10}
              fill={'#000000'}
              viewBox="3 0 18 12"
            />
          </HorizontalScrollButton> */}
          <DesktopTableWrapper>
            <TableWrapper
              tableHasBorder={false}
              tableHasBoxShadow={false}
              noItemsText={__('Nenašli sa žiadne objednávky')}
              headers={headers}
              rows={orderRows}
            />
          </DesktopTableWrapper>
          <MobileTableWrapper>
            {orders &&
              orders.map((order, index) => {
                const {
                  id,
                  date,
                  status,
                  sumPrice,
                  ordernr,
                  url,
                } = getOrderRowInfo(order);
                const color = getOrderColor(status, theme);
                return (
                  <OrderMobile key={index}>
                    <>
                      <RowWrapper>
                        <Desciption>{__('Obj. číslo')}</Desciption>
                        <LinkToRequest to={url}>{ordernr}</LinkToRequest>
                      </RowWrapper>
                      <RowWrapper>
                        <Desciption>{__('Dátum a čas')}</Desciption>
                        <Value>{date}</Value>
                      </RowWrapper>
                      <RowWrapper>
                        <Desciption>{__('Suma')}</Desciption>
                        <Value>{`${sumPrice} €`}</Value>
                      </RowWrapper>
                      <RowWrapper>
                        <Desciption>{__('Faktúra')}</Desciption>
                        {renderMobileDownloadIcon()}
                      </RowWrapper>
                      <RowWrapper>
                        <Desciption color={color}>{__('Stav')}</Desciption>
                        {renderMobileStatus(status, color)}
                      </RowWrapper>
                    </>
                  </OrderMobile>
                );
              })}
          </MobileTableWrapper>
        </>
      )}
    </Wrapper>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexRow)`
  flex-flow: row wrap;
  margin-bottom: ${rem(24)};
  animation: ${animateIn} 0.2s ease;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mediab.m810`
      margin: 0;
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
  `};
`;

const DesktopTableWrapper = styled.div`
  width: 100%;

  ${({ theme }) => theme.mediab.l1150`
    display: none;
  `}
`;

const MobileTableWrapper = styled.div`
  display: none;
  width: 100%;

  ${({ theme }) => theme.mediab.l1150`
    display: block;
  `}
`;

const OrderMobile = styled.div`
  display: block;
  width: 100%;
  border-bottom: ${rem(2)} solid #eeeeee;
  margin-top: ${rem(24)};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const textLikeCss = css`
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 300;
  font-size: ${rem(14)};
  line-height: ${rem(21)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const Desciption = styled.p<{}>`
  ${textLikeCss};
  font-weight: 500;
`;

const Value = styled.p<{ color?: string; textAlign?: string }>`
  ${textLikeCss};

  font-weight: ${({ theme, color }) => (color ? 500 : 300)};
  color: ${({ theme, color }) => (color ? color : theme.colors.textPrimary)};
`;

const LinkToRequest = styled(Link)`
  ${textLikeCss};
  font-weight: 500;
  text-decoration: underline;
`;

const RowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${rem(16)};

  &:last-child {
    margin-bottom: ${rem(24)};
  }
`;

const HorizontalScrollButton = styled.div`
  display: none;
  position: absolute;
  top: ${rem(18)};
  right: ${rem(16)};
  height: ${rem(24)};
  width: ${rem(48)};
  /* display: flex; */
  justify-content: center;
  align-items: center;
  border-radius: ${rem(12)};
  box-shadow: 0px 1px 2px #00000029;
  border: 1px solid #cccccc;
  opacity: 0.8;

  ${({ theme }) => theme.mediab.l925`
      display: flex;
  `};
`;

const FileWrapper = styled(FlexRowCenterVertical)`
  cursor: pointer;

  svg {
    fill: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const WithThemeOrders: any = withTheme(Orders);
export { WithThemeOrders as Orders };
